import {
    Alignment,
    Material,
    Object3D,
    VerticalAlignment,
    WonderlandEngine,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {
    Align,
    Display,
    FlexDirection,
    Justify,
    ReactUiBase,
} from '@wonderlandengine/react-ui';
import {
    Column,
    Text,
    Row,
    MaterialContext,
    ThemeContext,
    Container,
    Button,
} from '@wonderlandengine/react-ui/components';

import React, {useState, useEffect, useRef} from 'react';

import profile, {getXPForLevel, getXPPercentToNext} from './utils/profile.js';
import type {GameAnalytics as GA} from 'gameanalytics';

declare var GameAnalytics: typeof GA;
import {CHARACTERS, Character, onCharactersReady} from './register-character.js';

import {CharacterGrid, ViverseAvatar} from './custom-components.js';
import {CustomProgressBar} from './custom-components.js';
import {ConfirmationDialog} from './confirmation-dialog.js';
import {purchases} from 'uber-sdk/purchases.js';
import {user} from 'uber-sdk/user.js';

declare global {
    var TARGET: string;
}

const HEYVR_TO_EUR = 4.5;

export const App = (props: {comp: MenuPanel}) => {
    const [username, setUsername] = useState<string>('');
    const [level, setLevel] = useState<number>(1);
    const [xp, setXP] = useState<number>(0);
    const [xpPercentToNext, setXPPercentToNext] = useState<number>(0);
    const [confirmation, setConfirmation] = useState<{
        res: () => void;
        rej: () => void;
        name: string;
    } | null>();

    const comp = props.comp;

    const refreshUi = () => {
        profile.init().then(() => {
            if (user.isLoggedIn) {
                GameAnalytics.addDesignEvent('account:login:success');
            }
            setUsername(profile.getUsername());
            setLevel(profile.getLevel());
            setXP(profile.getXP());
            setXPPercentToNext(getXPPercentToNext(profile.getXP()));
        });
    };

    let [initialized, setInitialized] = useState(false);
    useEffect(() => {
        refreshUi();
        user.onAuthChange.add(refreshUi);

        onCharactersReady.once(() => {
            /* Toggle initialized, which triggers a re-render and applies character meshes
             * deep inside the CharacterDisplay component */
            setInitialized(!initialized);
        });

        return () => {
            user.onAuthChange.remove(refreshUi);
        };
    }, []);

    const theme = {
        colors: {
            background: comp.panelSecondary,

            primary: comp.panelSecondary,
            primaryActive: comp.panelSecondaryActive,
            primaryHovered: comp.panelSecondaryHovered,

            borderPrimary: 0,
            borderPrimaryActive: 0,
            borderPrimaryHovered: 0xffffffff,
            text: 0xffffffff,
        },
    };
    const openItemShop = (itemId: string) => {
        GameAnalytics.addDesignEvent('purchase:' + itemId + ':fail');
        /* Player did not have enough coins, open the shop */
        comp.engine.xr?.session.end().then(async () => {
            const url = await purchases.getItemURL(itemId);
            const w = window.open(url, '_blank');
            if (w) w.focus();
        });
    };

    const characters = Array.from(CHARACTERS.values());
    const [selectedCharacter, setSelectedCharacter] = useState<number>(0);
    const clickCallback = (event: any, globalIndex: number, character: Character) => {
        const name = character.name!;
        const isBuyable = purchases.hasProviders() && character.price !== 0;
        if (isBuyable) {
            const itemId = 'avatar-' + name.toLowerCase();
            const purchasedItem =
                itemId == 'avatar-dummy' || purchases.isItemPurchased(itemId);

            if (!purchasedItem) {
                GameAnalytics.addDesignEvent('purchase:' + itemId + ':intent');
                new Promise<void>((res, rej) => setConfirmation({res, rej, name}))
                    .then(() => {
                        GameAnalytics.addDesignEvent('purchase:' + itemId + ':confirm');
                        purchases
                            .purchaseItem(itemId)
                            .then((success) => {
                                if (success) {
                                    //add logic to show purchased
                                    GameAnalytics.addBusinessEvent(
                                        'EUR',
                                        Math.floor(HEYVR_TO_EUR * (character.price ?? 0)),
                                        'avatar',
                                        itemId,
                                        'character-selection'
                                    );
                                    GameAnalytics.addDesignEvent(
                                        'purchase:' + itemId + ':success'
                                    );
                                } else {
                                    openItemShop(itemId);
                                }
                            })
                            .catch(() => openItemShop(itemId));
                    })
                    .catch(() => {
                        GameAnalytics.addDesignEvent('purchase:' + itemId + ':cancel');
                    })
                    .finally(() => setConfirmation(null));

                return;
            } else {
                setSelectedCharacter(globalIndex);
            }
        }

        const itemUnlocked = !isBuyable && (character.level ?? 0) <= profile.getLevel();
        if (itemUnlocked) {
            setSelectedCharacter(globalIndex);
            profile.setAvatar(characters[globalIndex].name);
        }
    };

    const onPlayClicked = () => {
        GameAnalytics.addDesignEvent('game:button-play');
        // TODO: Show ad first
        comp.engine.scene.load(
            (TARGET === 'discord' ? '/.proxy/' : '') + 'PrototypeMap.bin'
        );
    };

    const onLoginClick = () => {
        GameAnalytics.addDesignEvent('button:open-login');
        user.requestLogin();
    };

    return (
        <MaterialContext.Provider value={props.comp}>
            <ThemeContext.Provider value={theme}>
                <Container>
                    <Column padding="8%" width="100%" rowGap={55}>
                        <CustomProgressBar
                            value={xpPercentToNext + 0.1}
                            width="90%"
                            height={60}
                            rounding={20}
                            bgColor={comp.panelSecondary}
                            fgColor={comp.panelSecondaryActive}
                            alignSelf={Align.Center}
                        >
                            <Row
                                width="100%"
                                height="100%"
                                alignItems={Align.Center}
                                justifyContent={Justify.SpaceBetween}
                            >
                                <Text
                                    fontSize={24}
                                    color="#000000"
                                    textAlign="left"
                                    marginLeft={20}
                                >
                                    {'Lvl: ' + level}
                                </Text>
                                <Text fontSize={24} color="#999999ff" textAlign="center">
                                    {username}
                                </Text>
                                <Text
                                    fontSize={24}
                                    color="#000000"
                                    textAlign="right"
                                    marginRight={20}
                                >
                                    {xp + ' / ' + getXPForLevel(level + 1)}
                                </Text>
                            </Row>
                        </CustomProgressBar>
                        <Column width="100%" rowGap={20}>
                            {TARGET == 'viverse' ? (
                                <ViverseAvatar url={profile.getAvatar()}></ViverseAvatar>
                            ) : (
                                <CharacterGrid
                                    characters={characters}
                                    selectedCharacter={selectedCharacter}
                                    clickCallback={clickCallback}
                                    comp={comp}
                                    profile={profile}
                                />
                            )}
                        </Column>
                        <Row top={5} alignSelf={Align.Center} gap={10}>
                            {!user.isLoggedIn && window.heyVR && (
                                <Button
                                    backgroundColor={0x13a3d4ff}
                                    hovered={{
                                        backgroundColor: 0x186078ff,
                                    }}
                                    active={{
                                        backgroundColor: 0x29bdc2ff,
                                    }}
                                    rounding={10}
                                    padding={10}
                                    onClick={onLoginClick}
                                    display={Display.Flex}
                                    alignContent={Align.Center}
                                >
                                    <Text fontSize={24} color="#000000">
                                        Log in
                                    </Text>
                                    <Text fontSize={18} color="#000000">
                                        to save progress
                                    </Text>
                                </Button>
                            )}
                            <Button
                                backgroundColor={0x00a31bff}
                                hovered={{
                                    backgroundColor: 0x007513ff,
                                }}
                                active={{
                                    backgroundColor: 0x1ebd38ff,
                                }}
                                rounding={10}
                                padding={10}
                                paddingLeft={60}
                                paddingRight={60}
                                onClick={onPlayClicked}
                                display={Display.Flex}
                                alignContent={Align.Center}
                            >
                                <Text fontSize={30} color="#ffffffff" textAlign="center">
                                    Play!
                                </Text>
                            </Button>
                        </Row>
                    </Column>
                    {confirmation && (
                        <ConfirmationDialog
                            onConfirm={confirmation.res}
                            onCancel={confirmation.rej}
                            text={`Do you want to\nbuy ${confirmation.name}?`}
                        />
                    )}
                </Container>
            </ThemeContext.Provider>
        </MaterialContext.Provider>
    );
};

export class MenuPanel extends ReactUiBase {
    static TypeName = 'menu-panel';
    static InheritProperties = true;

    @property.object()
    meshObj!: Object3D;

    @property.material()
    material!: Material;

    @property.material()
    panelMat!: Material;

    @property.material()
    panelHoverMat!: Material;

    @property.material()
    panelActiveMat!: Material;

    @property.color(1, 1, 1, 1)
    textColor!: Float32Array;

    @property.color(1, 1, 1, 1)
    panelSecondary!: Float32Array;

    @property.color(1, 1, 1, 1)
    panelSecondaryHovered!: Float32Array;

    @property.color(1, 1, 1, 1)
    panelSecondaryActive!: Float32Array;

    @property.object()
    refObj!: Object3D;

    ready = false;

    onReady() {
        this.ready = true;
    }

    render() {
        onCharactersReady.add(this.onReady.bind(this));
        return <App comp={this} />;
    }
}
